import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Gear, Camera, Sparkle, Cube, Gauge, Package, Cpu, Terminal, Copy, Check } from "@phosphor-icons/react";

const STEP_ICONS = [Camera, Sparkle, Cube, Gauge, Package];
const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

const AI_FIELDS = [
  ["item_condition_type", "Condition type"],
  ["condition_level", "Condition grade"],
  ["attribution_basis", "Attribution basis"],
  ["dominant_color", "Dominant color"],
  ["material", "Materials"],
  ["period_era", "Period / era"],
];

export default function UnderTheHoodSheet({ open, onClose, listing }) {
  const [schema, setSchema] = useState(null);
  const [readiness, setReadiness] = useState([]);
  const [copied, setCopied] = useState(false);
  const curlCmd = `curl ${API}/engine/schema`;
  const copyCurl = async () => {
    try {
      await navigator.clipboard.writeText(curlCmd);
      setCopied(true);
      toast.success("Copied — paste it in your terminal");
      setTimeout(() => setCopied(false), 1600);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (!open) return;
    api.get("/engine/schema").then((r) => setSchema(r.data)).catch(() => {});
    if (listing?.id) {
      api.get(`/listings/${listing.id}/readiness`).then((r) => setReadiness(r.data.platforms || [])).catch(() => {});
    }
  }, [open, listing?.id]);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto bg-[#FDFBF7] border-l border-[#E5E0D6] p-0"
        data-testid="under-the-hood-sheet"
      >
        <div className="p-6 space-y-7">
          <SheetHeader className="space-y-1.5 text-left">
            <SheetTitle className="font-serif-display text-2xl font-semibold flex items-center gap-2">
              <Gear size={22} weight="fill" className="text-[#B47A3C]" /> Under the Hood
            </SheetTitle>
            <p className="text-sm text-[#6B7280]">
              The engine behind Vintage Vantage — running live on this piece. Not a black box.
            </p>
          </SheetHeader>

          {schema && (
            <div className="flex flex-wrap gap-2" data-testid="uth-models">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#0D1B2A] text-[#FDFBF7] rounded-full px-3 py-1.5">
                <Cpu size={13} weight="fill" /> Vision · {schema.models.vision}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#0D1B2A] text-[#FDFBF7] rounded-full px-3 py-1.5">
                <Cpu size={13} weight="fill" /> Text · {schema.models.text}
              </span>
            </div>
          )}

          <div className="rounded-md border border-[#EAD9C2] bg-[#FBF3E9] p-3.5 text-xs leading-relaxed text-[#5B4636]" data-testid="uth-how-it-works">
            Every listing flows through <strong>one canonical schema</strong>. A vision model reads your photos into
            structured attributes — you stay the editor. Each marketplace is just a <em>dialect</em> of that schema, so
            readiness is a deterministic score over required + recommended fields — no hidden heuristics, and we never
            fake a marketplace push. The endpoint below returns this exact config. Nothing here is a black box.
          </div>

          {schema && (
            <section data-testid="uth-pipeline">
              <p className="label-caps mb-3">Pipeline</p>
              <ol className="space-y-3">
                {schema.pipeline.map((s, i) => {
                  const Icon = STEP_ICONS[i] || Cube;
                  return (
                    <li key={i} className="flex gap-3">
                      <div className="h-8 w-8 rounded-full bg-[#FBF3E9] border border-[#EAD9C2] flex items-center justify-center shrink-0 text-[#B47A3C]">
                        <Icon size={16} weight="fill" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-[#0D1B2A]">{i + 1}. {s.step}</p>
                        <p className="text-xs text-[#6B7280] leading-snug">{s.detail}</p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>
          )}

          <section data-testid="uth-extracted">
            <p className="label-caps mb-3">AI-extracted attributes · this piece</p>
            <div className="grid grid-cols-2 gap-2">
              {AI_FIELDS.map(([k, label]) => (
                <div key={k} className="rounded-md border border-[#E5E0D6] bg-white p-2.5">
                  <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold">{label}</p>
                  <p className="text-sm font-medium text-[#0D1B2A] truncate" title={fmt(listing?.[k])}>{fmt(listing?.[k])}</p>
                </div>
              ))}
              <div className="rounded-md border border-[#E5E0D6] bg-white p-2.5 col-span-2">
                <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold">Dimensions (inches)</p>
                <p className="text-sm font-medium text-[#0D1B2A]">
                  {fmt(listing?.width_inches)} W × {fmt(listing?.depth_inches)} D × {fmt(listing?.height_inches)} H
                </p>
              </div>
            </div>
          </section>

          {schema && (
            <section data-testid="uth-scoring">
              <p className="label-caps mb-3">Readiness scoring</p>
              <div className="rounded-md bg-[#0D1B2A] text-[#E7EDF3] p-3 font-mono text-[11px] leading-relaxed space-y-1">
                <p>{schema.scoring.score_formula}</p>
                <p className="text-[#9DB2C6]">{schema.scoring.ready_rule}</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-2 mt-3">
                {readiness.map((p) => (
                  <div key={p.key} className="rounded-md border border-[#E5E0D6] bg-white p-3" data-testid={`uth-score-${p.key}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-sm text-[#0D1B2A]">{p.name}</span>
                      <span className={`text-sm font-bold ${p.ready ? "text-[#047857]" : "text-[#B45309]"}`}>{p.score}%</span>
                    </div>
                    <p className="text-xs text-[#6B7280] mt-0.5">
                      required {p.required_met}/{p.required_total} · recommended {p.recommended_met}/{p.recommended_total}
                      {p.ready ? " · ready" : ""}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {schema && (
            <section data-testid="uth-mapping">
              <p className="label-caps mb-1">One schema → every marketplace's dialect</p>
              <p className="text-xs text-[#6B7280] mb-3">
                Bold = required on that marketplace. One canonical field, mapped to each platform's label.
              </p>
              <div className="overflow-x-auto rounded-md border border-[#E5E0D6]">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-[#F5F3ED]">
                      <th className="text-left font-semibold px-2.5 py-2 text-[#0D1B2A]">Canonical field</th>
                      {schema.platforms.map((p) => (
                        <th key={p.key} className="text-left font-semibold px-2.5 py-2 text-[#0D1B2A] whitespace-nowrap">{p.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {schema.canonical_fields.map((f, idx) => (
                      <tr key={f.key} className={idx % 2 ? "bg-white" : "bg-[#FDFBF7]"}>
                        <td className="px-2.5 py-2 font-medium text-[#0D1B2A] whitespace-nowrap">{f.label}</td>
                        {schema.platforms.map((p) => {
                          const cell = f.platforms[p.key];
                          return (
                            <td key={p.key} className="px-2.5 py-2 text-[#525252]">
                              {cell ? (
                                <span className={cell.tier === "required" ? "font-semibold text-[#0D1B2A]" : ""}>{cell.label}</span>
                              ) : (
                                <span className="text-[#C4C4C4]">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section data-testid="uth-inspect">
            <p className="label-caps mb-2">Inspect it yourself</p>
            <p className="text-xs text-[#6B7280] mb-2">Public, read-only. Hit the engine's schema endpoint from your terminal:</p>
            <div className="flex items-stretch gap-2">
              <code className="flex-1 rounded-md bg-[#0D1B2A] text-[#E7EDF3] font-mono text-[11px] px-3 py-2.5 overflow-x-auto whitespace-nowrap flex items-center gap-2">
                <Terminal size={13} className="text-[#9DB2C6] shrink-0" /> {curlCmd}
              </code>
              <button
                onClick={copyCurl}
                data-testid="uth-copy-curl"
                className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-[#0D1B2A] bg-white text-[#0D1B2A] text-xs font-semibold px-3 hover:bg-[#0D1B2A] hover:text-white transition-colors"
              >
                {copied ? <Check size={14} weight="bold" /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}
